// 6-axis health radar card (ADR-018 / SPEC-015 §3.3). Renders ONE chart.js
// Radar over the health_* series, each axis 0-100. The plotted value per axis is
// the AVERAGE over the selected range (SPEC-015 §3.3 — average is steadier than
// "latest"). Styled like TrendChart (dark theme). Empty state when every
// health_* series is empty.

import { Radar } from "react-chartjs-2";
import type { ChartData, ChartOptions } from "chart.js";
import { useI18n } from "@faceheart/i18n";
import { ensureChartsRegistered, HEALTH_AXES } from "./chartSetup";
import type { MeasurementSeriesResponse } from "@faceheart/api-types";

ensureChartsRegistered();

interface Props {
  /** All trend series from the measurements endpoint; we pick the health_* ones. */
  series: MeasurementSeriesResponse["series"];
  /** Larger render height for the zoom modal; defaults to the card height. */
  height?: string;
}

// average of a series' point values, or null when the series has no points.
function avg(points: { v: number }[] | undefined): number | null {
  if (!points || points.length === 0) return null;
  const sum = points.reduce((acc, p) => acc + p.v, 0);
  return sum / points.length;
}

export function RadarCard({ series, height = "h-96" }: Props) {
  const { t } = useI18n("admin");
  const title = t("trends.radar.title");

  const seriesFor = (key: string) => series.find((s) => s.metric === key);
  const values = HEALTH_AXES.map((a) => avg(seriesFor(a.key)?.points));
  const hasAny = values.some((v) => v !== null);
  const labels = HEALTH_AXES.map((a) => t(`trends.metrics.${a.key}`));

  const data: ChartData<"radar"> = {
    labels,
    datasets: [
      {
        label: title,
        data: values.map((v) => v ?? 0),
        borderColor: "#00d4ff",
        backgroundColor: "rgba(0, 212, 255, 0.18)",
        borderWidth: 1.5,
        pointBackgroundColor: "#00d4ff",
        pointRadius: 3,
        fill: true,
      },
    ],
  };

  const opts: ChartOptions<"radar"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#091f38",
        titleColor: "#e4f2ff",
        bodyColor: "#7ab3d0",
        borderColor: "#1a5580",
        borderWidth: 1,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 5,
        angleLines: { color: "rgba(13, 51, 85, 0.6)" },
        grid: { color: "rgba(13, 51, 85, 0.6)" },
        pointLabels: { color: "#7ab3d0", font: { size: 11 } },
        ticks: {
          color: "#3d6b8a",
          backdropColor: "transparent",
          stepSize: 1,
        },
      },
    },
  };

  return (
    <div className="bg-admin-bg2 border border-admin-border rounded-lg p-4 flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-sm text-admin-text2">{title}</h3>
        <div className="text-xs text-admin-text3">{t("trends.radar.scale")}</div>
      </div>
      <div className={height}>
        {hasAny ? (
          <Radar data={data} options={opts} />
        ) : (
          <div className="h-full flex items-center justify-center text-admin-text3 text-xs">
            {t("trends.noData", { label: title })}
          </div>
        )}
      </div>
    </div>
  );
}
